import { Grid, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { Cell, PieChart, Pie, Tooltip, ResponsiveContainer } from 'recharts';
import SouthEastIcon from '@mui/icons-material/SouthEast';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import { ThemePalette } from 'mui.theme';
import { FlexBox } from 'components/Containers/FlexBox';
import MultiColorCircularLoader from 'views/Production/components/BoxLoader/MultiColorCircularLoader';
import TableList from './TableList';
import { truncateTo2dp } from 'utils/helpers/general';
import { formatNumberToShortScale } from 'views/Production/Hauler/component/utils';
import { useEffect, useMemo } from 'react';
import { getDiffForUtilization, returnAnalysis } from 'utils/fuelAnalytics';
import useAppTheme from 'hooks/useAppTheme';
import useCustomerInfo from 'hooks/useCustomerInfo';
import { getUtilizationRowLabel } from 'views/Utilization/utils';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const _palette = clientThemePalette[CLIENT_THEME];

interface Props {
  title: string;
  isLoading: boolean;
  data: {
    total: number;
    totalDiff: number;
    productive: number;
    productiveDiff: number;
    idle: number;
    idleDiff: number;
  };
  setFuelConsumption: any;
}

const FuelConsumption = ({
  title,
  isLoading,
  data,
  setFuelConsumption,
}: Props) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme ? _palette.dark : _palette.light;
  const { unitSystem } = useCustomerInfo();

  const idleValue = useMemo(() => data.idle, [data]);
  const productiveValue = useMemo(() => data.productive, [data]);

  const dataProductiveDiff = useMemo(() => data.productiveDiff, [data]);
  const dataTotalDiff = useMemo(() => data.totalDiff, [data]);
  const dataTotal = useMemo(() => data.total, [data]);
  const dataIdleDiff = useMemo(() => data.idleDiff, [data]);

  const isProductiveBased = useMemo(
    () =>
      title === t('hauler_fuel_consumption') ||
      title === t('loader_fuel_consumption'),
    [title, t]
  );

  const isSame = useMemo(() => {
    if (isProductiveBased) {
      return (
        getDiffForUtilization(dataProductiveDiff, productiveValue) === 0 &&
        dataProductiveDiff !== 0
      );
    }

    return (
      getDiffForUtilization(dataTotalDiff, dataTotal) === 0 &&
      dataTotalDiff !== 0
    );
  }, [
    isProductiveBased,
    dataProductiveDiff,
    productiveValue,
    dataTotalDiff,
    dataTotal,
  ]);

  const isNotAvailable = useMemo(() => {
    if (isProductiveBased) {
      return (
        !dataProductiveDiff ||
        (productiveValue === 0 && dataProductiveDiff === 0) ||
        dataProductiveDiff === undefined ||
        dataProductiveDiff === -100
      );
    }

    return (
      !dataTotalDiff || dataTotalDiff === undefined || dataTotalDiff === -100
    );
  }, [isProductiveBased, dataProductiveDiff, productiveValue, dataTotalDiff]);

  const tableData = useMemo(
    () => [
      {
        name: 'Productive',
        displayName: t('productive'),
        time: formatNumberToShortScale(Number(truncateTo2dp(productiveValue))),
        direction:
          isSame || isNotAvailable || dataProductiveDiff === 0
            ? 'none'
            : dataProductiveDiff > 0
            ? 'up'
            : 'down',
        color: '#FF0068',
        fill: '#FF0068',
      },
      {
        name: 'Idle',
        displayName: t('idle'),
        time: formatNumberToShortScale(Number(truncateTo2dp(idleValue))),
        direction:
          isSame || isNotAvailable || dataIdleDiff === 0
            ? 'none'
            : dataIdleDiff > 0
            ? 'up'
            : 'down',
        color: '#FF9C2C',
        fill: '#FF9C2C',
      },
    ],
    [
      productiveValue,
      isSame,
      isNotAvailable,
      dataProductiveDiff,
      idleValue,
      dataIdleDiff,
      t,
    ]
  );

  const rechartList = useMemo(
    () => [
      {
        name: 'Productive',
        time: Number(truncateTo2dp(productiveValue)),
        direction:
          dataProductiveDiff === 0
            ? 'none'
            : dataProductiveDiff > 0
            ? 'up'
            : 'down',
        color: '#FF0068',
      },
      {
        name: 'Idle',
        time: Number(truncateTo2dp(idleValue)),
        direction:
          dataIdleDiff === 0 ? 'none' : dataIdleDiff > 0 ? 'up' : 'down',
        color: '#FF9C2C',
      },
    ],
    [productiveValue, dataProductiveDiff, idleValue, dataIdleDiff]
  );

  const allData = useMemo(() => {
    return {
      [`Total (${unitSystem === 'imperial' ? 'Gallons' : 'Litres'})`]:
        formatNumberToShortScale(truncateTo2dp(dataTotal)),
      'From Last period': returnAnalysis(dataTotalDiff),
      ...Object.fromEntries(
        tableData.map(({ name, time, direction }) => [
          name,
          // `${time} (${direction})`,
          `${time}`,
        ])
      ),
    };
  }, [unitSystem, dataTotal, dataTotalDiff, tableData]);

  useEffect(() => {
    setFuelConsumption(allData);
  }, [allData]);

  return (
    <Grid item lg={6} md={6}>
      <Box
        sx={{
          padding: 2,
          backgroundColor: enableDarkTheme
            ? palette.boxBackground
            : palette.toolBarBackground,
          borderRadius: '5px',
          border: `1px solid ${palette.borderGrey}`,
          minHeight: '400px',
          maxHeight: '400px',
          overflowY: 'auto',
        }}
      >
        <Typography
          sx={{
            fontSize: '13px',
            fontWeight: '600',
          }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            paddingTop: '0px',
          }}
        >
          {isLoading ? (
            <MultiColorCircularLoader height="300px" />
          ) : (
            <Grid container>
              <Grid item lg={6} sm={12}>
                <Box
                  sx={{
                    paddingTop: '20px',
                    minHeight: '200px',
                    maxHeight: '200px',
                  }}
                >
                  <FlexBox
                    sx={{
                      maxWidth: 'inherit',
                      height: '230px',
                      paddingTop: '20px',
                      marginRight: '10px',
                      paddingBottom: '10px',
                    }}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart width={400} height={400}>
                        <Pie
                          data={rechartList}
                          dataKey="time"
                          cx="50%"
                          cy="50%"
                          innerRadius={70}
                          outerRadius={90}
                        >
                          {rechartList.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          cursor={{ fill: 'rgba(0,0,0,0.1)' }}
                          wrapperStyle={{ outline: 'none', fontSize: '11px' }}
                          contentStyle={{
                            backgroundColor: 'rgba(0,0,0, 0.6)',
                            borderColor: 'rgba(255, 255, 255, 0.1)',
                            borderRadius: '4px',
                          }}
                          itemStyle={{
                            color: enableDarkTheme
                              ? ThemePalette.typography.wheat
                              : ThemePalette.typography.black,
                          }}
                          formatter={(value: any, name, props) => {
                            return [
                              `${formatNumberToShortScale(
                                Number(truncateTo2dp(value))
                              )} hrs`,
                              getUtilizationRowLabel(
                                t,
                                typeof name === 'string' ? name : undefined
                              ),
                            ];
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </FlexBox>
                </Box>
              </Grid>
              <Grid item lg={6} sm={12}>
                <Box>
                  <Typography
                    sx={{
                      fontSize: '13px',
                    }}
                  >
                    {t('cdp_total_label')}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '25px',
                    }}
                  >
                    {!isNotAvailable || dataTotalDiff !== 0 ? (
                      Number(dataTotalDiff) > 0 ? (
                        <NorthEastIcon
                          sx={{ color: '#029161', fontSize: 19 }}
                        />
                      ) : (
                        <SouthEastIcon
                          sx={{ color: '#EB5757', fontSize: 19 }}
                        />
                      )
                    ) : null}{' '}
                    {formatNumberToShortScale(Number(truncateTo2dp(dataTotal)))}
                    <span
                      style={{
                        fontSize: '11px',
                      }}
                    >
                      {' '}
                      {unitSystem === 'imperial'
                        ? t('fuel_usage_unit_gallons')
                        : t('fuel_usage_unit_litres')}
                    </span>
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '12px',
                    }}
                  >
                    <span
                      style={{
                        color: isSame
                          ? 'inherit'
                          : Number(dataTotalDiff) > 0
                          ? '#029161'
                          : '#EB5757',
                      }}
                    >
                      {isSame && !isNotAvailable
                        ? t('utilization_same')
                        : isProductiveBased
                        ? returnAnalysis(
                            getDiffForUtilization(
                              dataProductiveDiff,
                              productiveValue
                            )
                          )
                        : returnAnalysis(
                            getDiffForUtilization(dataTotalDiff, dataTotal)
                          )}
                    </span>{' '}
                    {t('from_last_period')}
                  </Typography>
                </Box>
                <TableList
                  data={tableData}
                  unit={
                    unitSystem === 'imperial'
                      ? t('fuel_usage_unit_gallons')
                      : t('fuel_usage_unit_litres')
                  }
                />
              </Grid>
            </Grid>
          )}
        </Box>
      </Box>
    </Grid>
  );
};

export default FuelConsumption;
